import { useState } from "react";
import { useDispatch } from "react-redux";
import { setUser, setEmailVerificationPending } from "../store/user/user.action";

// firebase
import { createUserWithEmailAndPassword, sendEmailVerification as firebaseSendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db, logUserAction } from '../utils/firebase/firebase.utils';
import { syncPublicProfile } from '../utils/firebase/publicProfile';
import { saveUserCache } from '../utils/cache/userCache';
import { serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { welcomeTokenAmount } from '../utils/tokens/welcomeTokens';

export const useCreateAccount = () => {
    const dispatch = useDispatch();
    const [error, setError] = useState(null);
    const [isPending, setIsPending] = useState(false);
    const [success, setSuccess] = useState(false);

    const createAccount = async (email, password, username, teamIndex, referralCode = null) => {
        setError(null);
        setSuccess(false);
        setIsPending(true);

        try {
            const res = await createUserWithEmailAndPassword(auth, email, password)

            if (!res) {
                setError("Error creating account, try again later.");
                throw new Error('Could not complete signup');
            }


            const tokenAmount = welcomeTokenAmount(referralCode);

            // Check if username is available using the Cloud Function
            const functions = getFunctions();
            const checkUsernameAvailability = httpsCallable(functions, 'checkUsernameAvailability');
            const availabilityResult = await checkUsernameAvailability({ username });
            
            if (!availabilityResult.data.isAvailable) {
                // Delete the Firebase Auth user since username is taken
                await res.user.delete();
                setError(availabilityResult.data.message || "This username is already taken. Please choose another.");
                setIsPending(false);
                return;
            }

            // Create user document first (without username, will be set via setUsername function)
            await setDoc(doc(db, 'users', res.user.uid), {
                teamIndex: teamIndex,
                collectionCardCount: 0,
                tokens: tokenAmount,
                createdAt: serverTimestamp()
            });
            await syncPublicProfile(res.user.uid, { collectionCardCount: 0 });

            // Use setUsername function to properly set username with transaction
            const setUsernameFunction = httpsCallable(functions, 'setUsername');
            const setUsernameResult = await setUsernameFunction({ username });
            
            if (!setUsernameResult.data.success) {
                // Delete the Firebase Auth user since username setting failed
                // Note: Firestore document will remain but user won't be able to sign in
                await res.user.delete();
                setError(setUsernameResult.data.message || "Failed to set username. Please try again.");
                setIsPending(false);
                return;
            }

            const user = res.user;
            const userData = {
              uid: user.uid,
              email: user.email,
              username: username,
              teamIndex: teamIndex,
              collectionCardCount: 0,
              tokens: tokenAmount,
              role: "user",
            };

            dispatch(setUser(userData));
            dispatch(setEmailVerificationPending(true));
            saveUserCache(userData, user.uid);
            // Activity log is newest-first. Write the welcome message first so
            // it is the oldest entry and sits at the bottom as the first event.
            await logUserAction(res.user.uid, "updatedAccount", { accountActionType: "newAccount", username: username });
            await logUserAction(res.user.uid, "welcomeTokensGranted", {
                prevCount: 0,
                newCount: tokenAmount,
                tokensAdded: tokenAmount,
                referral: !!referralCode
            });
            
            // Send email verification
            await firebaseSendEmailVerification(res.user);
            
            setError(null);
            setSuccess(true);
        }
        catch (err) {
            // If user was created but something failed, try to clean up
            if (auth.currentUser) {
                try {
                    await auth.currentUser.delete();
                } catch (deleteError) {
                    // Cleanup failed - ignore
                }
            }
            
            // Check for Firestore permission errors (username already taken)
            if (err.code === 'permission-denied' && err.message && err.message.includes('usernames')) {
                setError("This username is already taken. Please choose another.");
            }
            // Check for Firebase Functions errors
            else if (err.code === 'functions/not-found' || err.code === 'functions/unavailable') {
                setError("Service temporarily unavailable. Please try again later.");
            }
            // Check for Firebase Auth errors
            else if (err.message) {
                switch (err.message) {
                    case 'Firebase: Error (auth/email-already-in-use).':
                        setError("An account already exists with that email.");
                        break;
                    case 'Firebase: Error (auth/invalid-email).':
                        setError("An error occurred, please try again later.");
                        break;
                    case 'Firebase: Password should be at least 6 characters (auth/weak-password).':
                        setError("Password should be at least 6 characters.");
                        break;
                    default:
                        setError(err.message);
                }
            } else {
                setError("An error occurred during account creation. Please try again.");
            }
        }

        setIsPending(false)
    }

    const sendEmailVerification = async () => {
        if (auth.currentUser) {
            await firebaseSendEmailVerification(auth.currentUser);
        } else {
            throw new Error('No user is currently signed in');
        }
    }

    return { error, isPending, success, createAccount, sendEmailVerification }
}